package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import dev.lingvo.shared.ShotPushCheckDto
import dev.lingvo.shared.ShotCheckSeverity
import dev.lingvo.site.components.plainLocaleLabel
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Section
import dev.lingvo.shared.ShotPushPreflightDto
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.px
import org.w3c.dom.HTMLElement

/** Stable readability facts are translated; all other check messages remain server explanations. */
@Composable
fun PushChecks(slug: String, report: ShotPushPreflightDto?, hasSelection: Boolean = true, hasApproval: Boolean = true, renderChecks: List<ShotPushCheckDto> = emptyList()) {
    Div(attrs = { attr("data-push-checks", ""); attr("aria-live", "polite") }) {
        if (report == null) LMuted(when {
            !hasSelection -> t("app.projects.slug.screenshots.push_checks.tick_at_least_one_set_to", "Tick at least one set to check it")
            !hasApproval -> t("app.projects.slug.screenshots.push_checks.approve_the_selected_sets_to_run", "Approve the selected sets to run the store checks.")
            else -> t("app.projects.slug.screenshots.push_checks.checking_this_set", "Checking this set…")
        })
        val groups = (report?.checks ?: renderChecks).groupBy { check -> check.cell?.let { ReviewSetKey(it.locale, it.deviceClass) } }
        groups[null]?.distinct()?.forEach { check -> PushCheckRow(slug, check) }
        groups.filterKeys { it != null }.forEach { (key, checks) ->
            val set = requireNotNull(key)
            Section(attrs = { attr("data-check-set", "${set.locale}:${set.device.id}") }) {
                H4 { Text(tf("shots.review.check_set", "{language} · {device}",
                    "language" to plainLocaleLabel(set.locale), "device" to screenshotDeviceLabel(set.device))) }
                // The same fact may be repeated for every slot in a set. Each distinct repair stays linked.
                val distinct = checks.distinctBy { listOf(it.code, it.severity, it.message, it.fixPath, it.readability) }
                distinct.filter { it.severity != ShotCheckSeverity.GREEN }.forEach { PushCheckRow(slug, it) }
                val passed = distinct.filter { it.severity == ShotCheckSeverity.GREEN }
                if (passed.isNotEmpty()) TagElement<HTMLElement>("details", applyAttrs = {}) {
                    TagElement<HTMLElement>("summary", applyAttrs = { style { minHeight(40.px) } }) {
                        Text(tf("shots.review.passed_checks", "Passed checks ({count})", "count" to passed.size))
                    }
                    passed.forEach { PushCheckRow(slug, it) }
                }
            }
        }
    }
}

@Composable
private fun PushCheckRow(slug: String, check: ShotPushCheckDto) {
    Div(attrs = { attr("data-severity", check.severity.name.lowercase()) }) {
        val glyph = when (check.severity) { ShotCheckSeverity.GREEN -> "✓"; ShotCheckSeverity.AMBER -> "◷"; ShotCheckSeverity.RED -> "✕" }
        val severity = when (check.severity) {
            ShotCheckSeverity.GREEN -> t("app.projects.slug.screenshots.push_checks.passed", "Passed")
            ShotCheckSeverity.AMBER -> if (check.readability != null) t("app.projects.slug.screenshots.push_checks.needs_attention", "Needs attention") else t("app.projects.slug.screenshots.push_checks.waiting", "Waiting")
            ShotCheckSeverity.RED -> t("app.projects.slug.screenshots.push_checks.blocked", "Blocked")
        }
        Text("$glyph $severity: ")
        val path = safeScreenshotFixPath(slug, check.fixPath)
        if (path != null && check.severity != ShotCheckSeverity.GREEN) A(path, attrs = { style { minHeight(40.px); property("display", "flex"); property("align-items", "center"); property("overflow-wrap", "anywhere") } }) { Text(readabilityCheckMessage(check)) }
        else Text(readabilityCheckMessage(check))
        readabilityCheckDetails(check)?.let { detail -> LMuted(detail) }
    }
}

/** Limits upstream repair links to this project and prevents protocol-relative navigation. */
fun safeScreenshotFixPath(slug: String, path: String?): String? = path?.takeIf {
    it.startsWith("/app/projects/${encodeURIComponent(slug)}/") && !it.contains('\\') &&
        !it.contains(Regex("[\\u0000-\\u001f]")) && it.split('/').none { part -> part == ".." || part.equals("%2e%2e", true) }
}

private external fun encodeURIComponent(value: String): String
